import {
  Alert,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  HairlineCard,
  Icons,
  iconSize,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  StatusChip,
  TextField,
  Typography,
} from '@munifocus/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  EntryWarrantDetail,
  ObstructionResolution,
  ObstructionType,
  WarrantOutcome,
  WarrantTriggerPhase,
} from '@munifocus/contracts-gov';
import {
  isWarrantOpen,
  useDecideExtension,
  useDecideWarrant,
  useExecuteWarrant,
  useFileWarrant,
  useResolveObstruction,
  useWarrants,
} from '../lib/warrant-api';
import { useSubmitValidation, type Focusable } from '../lib/useSubmitValidation';
import { WarrantLog } from './WarrantLog';

/**
 * Entry Warrant sub-flow (brief §6 / design §4.14) — the officer-facing capture
 * UI for W1 (grounds, application, filing) and W2 (execution), plus the two
 * decision gates the court owns and the three ways an obstruction can resolve.
 *
 * The sub-flow is repeatable and can be launched from Stage 2/3/4, Step 5.5,
 * Step 7 and Step 8; the caller passes the trigger phase it sits in. Filing
 * pauses the main workflow (the API flips the file to `paused`) and a terminal
 * warrant state resumes it — the pause is called out in the UI because the
 * officer has no other signal that the file has stopped moving.
 *
 * Read-only history stays in {@link WarrantLog}, rendered underneath.
 */

const INTERVAL_OPTIONS = [7, 14, 21, 30] as const;
const OBSTRUCTION_TYPES: ObstructionType[] = ['occupant_refused', 'physical_barrier', 'other'];

/** `datetime-local` gives a zone-less local string; the API stores an instant. */
function toIso(localDateTime: string): string {
  const d = new Date(localDateTime);
  return Number.isNaN(d.getTime()) ? localDateTime : d.toISOString();
}

/** Follow-up interval picker — the four standard timers plus a custom value. */
function IntervalField({
  value,
  onChange,
  label,
  error,
  inputRef,
}: {
  value: number;
  onChange: (days: number) => void;
  label: string;
  /** Shown under the custom-days input — the only way to enter a bad value. */
  error?: string;
  inputRef?: (el: Focusable | null) => void;
}) {
  const { t } = useTranslation();
  const [custom, setCustom] = useState(!INTERVAL_OPTIONS.includes(value as never));

  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
      <FormControl fullWidth size="small">
        <InputLabel>{label}</InputLabel>
        <Select
          label={label}
          aria-label={label}
          value={custom ? 'custom' : String(value)}
          onChange={(e) => {
            const next = e.target.value as string;
            if (next === 'custom') {
              setCustom(true);
              return;
            }
            setCustom(false);
            onChange(Number(next));
          }}
        >
          {INTERVAL_OPTIONS.map((d) => (
            <MenuItem key={d} value={String(d)}>
              {t('gov.warrant.intervalOption', { days: d })}
            </MenuItem>
          ))}
          <MenuItem value="custom">{t('gov.warrant.intervalCustom')}</MenuItem>
        </Select>
      </FormControl>
      {custom && (
        <TextField
          fullWidth
          size="small"
          type="number"
          label={t('gov.warrant.customIntervalDays')}
          error={Boolean(error)}
          helperText={error}
          inputRef={inputRef}
          value={String(value)}
          onChange={(e) => onChange(Number(e.target.value))}
        />
      )}
    </Stack>
  );
}

// ── W1: grounds, application and filing ──────────────────────────────────────

function FileWarrantForm({
  fileId,
  triggerPhase,
  onDone,
}: {
  fileId: string;
  triggerPhase: WarrantTriggerPhase;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const fileWarrant = useFileWarrant(fileId);
  const [circumstances, setCircumstances] = useState('');
  const [priorEntryAttempts, setPriorEntryAttempts] = useState('');
  const [followUpIntervalDays, setFollowUpIntervalDays] = useState(14);

  const v = useSubmitValidation({
    circumstances: circumstances.trim() ? null : t('common.validation.required'),
    interval: followUpIntervalDays > 0 ? null : t('gov.warrant.intervalRequired'),
  });

  const submit = () =>
    fileWarrant.mutate(
      {
        triggerPhase,
        circumstances: circumstances.trim(),
        priorEntryAttempts: priorEntryAttempts.trim() || undefined,
        followUpIntervalDays,
      },
      { onSuccess: onDone },
    );

  return (
    <Stack spacing={2} sx={{ mt: 1 }}>
      <Typography variant="subtitle2">{t('gov.warrant.w1Title')}</Typography>
      {fileWarrant.isError && <Alert severity="error">{t('gov.warrant.saveError')}</Alert>}

      <TextField
        fullWidth
        required
        multiline
        minRows={3}
        label={t('gov.warrant.circumstances')}
        {...v.field('circumstances')}
        helperText={v.errors.circumstances ?? t('gov.warrant.circumstancesHelp')}
        value={circumstances}
        onChange={(e) => setCircumstances(e.target.value)}
      />
      <TextField
        fullWidth
        multiline
        minRows={2}
        size="small"
        label={t('gov.warrant.priorEntryAttempts')}
        helperText={t('gov.warrant.priorEntryAttemptsHelp')}
        value={priorEntryAttempts}
        onChange={(e) => setPriorEntryAttempts(e.target.value)}
      />
      <IntervalField
        label={t('gov.warrant.followUpInterval')}
        value={followUpIntervalDays}
        onChange={setFollowUpIntervalDays}
        error={v.errors.interval}
        inputRef={v.register('interval')}
      />
      <Typography variant="caption" color="text.secondary">
        {t('gov.warrant.filedDateNote')}
      </Typography>

      <Stack direction="row" spacing={1}>
        <Button
          variant="contained"
          disabled={fileWarrant.isPending}
          onClick={v.guard(submit)}
          startIcon={<Icons.Gavel size={iconSize.md} />}
        >
          {t('gov.warrant.fileAction')}
        </Button>
        <Button disabled={fileWarrant.isPending} onClick={onDone}>
          {t('common.actions.cancel')}
        </Button>
      </Stack>
    </Stack>
  );
}

// ── Decision gate: granted (with expiry) / denied ────────────────────────────

function DecisionForm({ fileId, warrant }: { fileId: string; warrant: EntryWarrantDetail }) {
  const { t } = useTranslation();
  const decide = useDecideWarrant(fileId);
  const [decision, setDecision] = useState<'granted' | 'denied'>('granted');
  const [expiryDate, setExpiryDate] = useState('');
  const [note, setNote] = useState('');

  // The API refuses a grant without an expiry — mirror that in the UI.
  const v = useSubmitValidation({
    expiryDate: decision === 'denied' || expiryDate ? null : t('common.validation.required'),
  });

  const submit = () =>
    decide.mutate({
      id: warrant.id,
      dto: {
        decision,
        expiryDate: decision === 'granted' ? expiryDate : undefined,
        note: note.trim() || undefined,
      },
    });

  return (
    <Stack spacing={2} sx={{ mt: 1 }}>
      <Typography variant="subtitle2">{t('gov.warrant.decisionTitle')}</Typography>
      {decide.isError && <Alert severity="error">{t('gov.warrant.saveError')}</Alert>}

      <FormControl fullWidth size="small">
        <InputLabel>{t('gov.warrant.decision')}</InputLabel>
        <Select
          label={t('gov.warrant.decision')}
          aria-label={t('gov.warrant.decision')}
          value={decision}
          onChange={(e) => setDecision(e.target.value as 'granted' | 'denied')}
        >
          <MenuItem value="granted">{t('gov.warrant.decisionGranted')}</MenuItem>
          <MenuItem value="denied">{t('gov.warrant.decisionDenied')}</MenuItem>
        </Select>
      </FormControl>

      {decision === 'granted' ? (
        <TextField
          fullWidth
          size="small"
          required
          type="date"
          label={t('gov.warrant.expiryDateLabel')}
          {...v.field('expiryDate')}
          slotProps={{ inputLabel: { shrink: true } }}
          value={expiryDate}
          onChange={(e) => setExpiryDate(e.target.value)}
        />
      ) : (
        <Alert severity="info">{t('gov.warrant.deniedNote')}</Alert>
      )}

      <TextField
        fullWidth
        size="small"
        label={t('gov.warrant.decisionNote')}
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />

      <Stack direction="row">
        <Button variant="contained" disabled={decide.isPending} onClick={v.guard(submit)}>
          {t('gov.warrant.decisionAction')}
        </Button>
      </Stack>
    </Stack>
  );
}

// ── W2: execution — entry made, or obstructed ────────────────────────────────

function ExecutionForm({ fileId, warrant }: { fileId: string; warrant: EntryWarrantDetail }) {
  const { t } = useTranslation();
  const execute = useExecuteWarrant(fileId);
  const [outcome, setOutcome] = useState<WarrantOutcome>('entered');
  const [officersPresent, setOfficersPresent] = useState('');
  const [accompaniedBy, setAccompaniedBy] = useState('');
  const [copyServed, setCopyServed] = useState(false);
  const [obstructionType, setObstructionType] = useState<ObstructionType>('occupant_refused');
  const [obstructionNote, setObstructionNote] = useState('');
  const [attemptedAt, setAttemptedAt] = useState('');
  const [policeRequested, setPoliceRequested] = useState(false);

  const obstructed = outcome !== 'entered';
  const v = useSubmitValidation({
    obstructionNote: !obstructed || obstructionNote.trim() ? null : t('common.validation.required'),
    attemptedAt: !obstructed || attemptedAt ? null : t('common.validation.required'),
  });

  const submit = () =>
    execute.mutate({
      id: warrant.id,
      dto:
        outcome === 'entered'
          ? {
              outcome,
              officersPresent: officersPresent.trim() || undefined,
              accompaniedBy: accompaniedBy.trim() || undefined,
              copyServed,
            }
          : {
              outcome,
              officersPresent: officersPresent.trim() || undefined,
              obstruction: {
                type: obstructionType,
                note: obstructionNote.trim(),
                attemptedAt: toIso(attemptedAt),
                policeRequested,
              },
            },
    });

  return (
    <Stack spacing={2} sx={{ mt: 1 }}>
      <Typography variant="subtitle2">{t('gov.warrant.w2Title')}</Typography>
      {execute.isError && <Alert severity="error">{t('gov.warrant.saveError')}</Alert>}

      <FormControl fullWidth size="small">
        <InputLabel>{t('gov.warrant.outcome')}</InputLabel>
        <Select
          label={t('gov.warrant.outcome')}
          aria-label={t('gov.warrant.outcome')}
          value={outcome}
          onChange={(e) => setOutcome(e.target.value as WarrantOutcome)}
        >
          <MenuItem value="entered">{t('gov.warrant.outcomeEntered')}</MenuItem>
          <MenuItem value="obstructed">{t('gov.warrant.outcomeObstructed')}</MenuItem>
        </Select>
      </FormControl>

      <TextField
        fullWidth
        size="small"
        label={t('gov.warrant.officersPresent')}
        value={officersPresent}
        onChange={(e) => setOfficersPresent(e.target.value)}
      />

      {outcome === 'entered' ? (
        <>
          <TextField
            fullWidth
            size="small"
            label={t('gov.warrant.accompaniedBy')}
            value={accompaniedBy}
            onChange={(e) => setAccompaniedBy(e.target.value)}
          />
          <FormControlLabel
            control={
              <Checkbox checked={copyServed} onChange={(e) => setCopyServed(e.target.checked)} />
            }
            label={t('gov.warrant.copyServed')}
          />
        </>
      ) : (
        <>
          <FormControl fullWidth size="small">
            <InputLabel>{t('gov.warrant.obstructionType')}</InputLabel>
            <Select
              label={t('gov.warrant.obstructionType')}
              aria-label={t('gov.warrant.obstructionType')}
              value={obstructionType}
              onChange={(e) => setObstructionType(e.target.value as ObstructionType)}
            >
              {OBSTRUCTION_TYPES.map((o) => (
                <MenuItem key={o} value={o}>
                  {t(`gov.warrant.obstructionTypeOption.${o}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            required
            multiline
            minRows={2}
            size="small"
            label={t('gov.warrant.obstructionNote')}
            {...v.field('obstructionNote')}
            value={obstructionNote}
            onChange={(e) => setObstructionNote(e.target.value)}
          />
          <TextField
            fullWidth
            required
            size="small"
            type="datetime-local"
            label={t('gov.warrant.attemptedAt')}
            {...v.field('attemptedAt')}
            slotProps={{ inputLabel: { shrink: true } }}
            value={attemptedAt}
            onChange={(e) => setAttemptedAt(e.target.value)}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={policeRequested}
                onChange={(e) => setPoliceRequested(e.target.checked)}
              />
            }
            label={t('gov.warrant.policeRequested')}
          />
        </>
      )}

      <Stack direction="row">
        <Button variant="contained" disabled={execute.isPending} onClick={v.guard(submit)}>
          {t('gov.warrant.executeAction')}
        </Button>
      </Stack>
    </Stack>
  );
}

// ── Obstruction resolution: re-attempt / extend / close ───────────────────────

function ObstructionForm({ fileId, warrant }: { fileId: string; warrant: EntryWarrantDetail }) {
  const { t } = useTranslation();
  const resolve = useResolveObstruction(fileId);
  const [resolution, setResolution] = useState<ObstructionResolution>('reattempt');
  const [reason, setReason] = useState('');
  const [extensionFiledAt, setExtensionFiledAt] = useState('');
  const [followUpIntervalDays, setFollowUpIntervalDays] = useState(14);

  // An extension needs all three particulars — the API rejects it otherwise.
  // The interval matters here because clearing the custom input yields 0/NaN,
  // which would otherwise sail past a non-empty check the way W1 already guards.
  const extending = resolution === 'extend';
  const v = useSubmitValidation({
    reason: !extending || reason.trim() ? null : t('common.validation.required'),
    extensionFiledAt: !extending || extensionFiledAt ? null : t('common.validation.required'),
    interval: !extending || followUpIntervalDays > 0 ? null : t('gov.warrant.intervalRequired'),
  });

  const submit = () =>
    resolve.mutate({
      id: warrant.id,
      dto:
        resolution === 'extend'
          ? { resolution, reason: reason.trim(), extensionFiledAt, followUpIntervalDays }
          : { resolution },
    });

  return (
    <Stack spacing={2} sx={{ mt: 1 }}>
      <Typography variant="subtitle2">{t('gov.warrant.obstructionTitle')}</Typography>
      {warrant.obstruction && (
        <Alert severity="warning">
          {t(`gov.warrant.obstructionTypeOption.${warrant.obstruction.type}`)} —{' '}
          {warrant.obstruction.note}
        </Alert>
      )}
      {resolve.isError && <Alert severity="error">{t('gov.warrant.saveError')}</Alert>}

      <FormControl fullWidth size="small">
        <InputLabel>{t('gov.warrant.resolution')}</InputLabel>
        <Select
          label={t('gov.warrant.resolution')}
          aria-label={t('gov.warrant.resolution')}
          value={resolution}
          onChange={(e) => setResolution(e.target.value as ObstructionResolution)}
        >
          <MenuItem value="reattempt">{t('gov.warrant.resolutionReattempt')}</MenuItem>
          <MenuItem value="extend">{t('gov.warrant.resolutionExtend')}</MenuItem>
          <MenuItem value="close">{t('gov.warrant.resolutionClose')}</MenuItem>
        </Select>
      </FormControl>

      {resolution === 'extend' && (
        <>
          <TextField
            fullWidth
            required
            multiline
            minRows={2}
            size="small"
            label={t('gov.warrant.extensionReason')}
            {...v.field('reason')}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <TextField
            fullWidth
            required
            size="small"
            type="date"
            label={t('gov.warrant.extensionFiledAt')}
            {...v.field('extensionFiledAt')}
            slotProps={{ inputLabel: { shrink: true } }}
            value={extensionFiledAt}
            onChange={(e) => setExtensionFiledAt(e.target.value)}
          />
          <IntervalField
            label={t('gov.warrant.followUpInterval')}
            value={followUpIntervalDays}
            onChange={setFollowUpIntervalDays}
            error={v.errors.interval}
            inputRef={v.register('interval')}
          />
        </>
      )}
      {resolution === 'close' && <Alert severity="info">{t('gov.warrant.closeNote')}</Alert>}

      <Stack direction="row">
        <Button variant="contained" disabled={resolve.isPending} onClick={v.guard(submit)}>
          {t('gov.warrant.resolveAction')}
        </Button>
      </Stack>
    </Stack>
  );
}

// ── Extension decision: granted (new expiry) / denied ─────────────────────────

function ExtensionDecisionForm({
  fileId,
  warrant,
}: {
  fileId: string;
  warrant: EntryWarrantDetail;
}) {
  const { t } = useTranslation();
  const decide = useDecideExtension(fileId);
  const [decision, setDecision] = useState<'granted' | 'denied'>('granted');
  const [newExpiry, setNewExpiry] = useState('');
  const [note, setNote] = useState('');

  const v = useSubmitValidation({
    newExpiry: decision === 'denied' || newExpiry ? null : t('common.validation.required'),
  });

  const submit = () =>
    decide.mutate({
      id: warrant.id,
      dto: {
        decision,
        newExpiry: decision === 'granted' ? newExpiry : undefined,
        note: note.trim() || undefined,
      },
    });

  return (
    <Stack spacing={2} sx={{ mt: 1 }}>
      <Typography variant="subtitle2">{t('gov.warrant.extensionTitle')}</Typography>
      {decide.isError && <Alert severity="error">{t('gov.warrant.saveError')}</Alert>}

      <FormControl fullWidth size="small">
        <InputLabel>{t('gov.warrant.extensionDecision')}</InputLabel>
        <Select
          label={t('gov.warrant.extensionDecision')}
          aria-label={t('gov.warrant.extensionDecision')}
          value={decision}
          onChange={(e) => setDecision(e.target.value as 'granted' | 'denied')}
        >
          <MenuItem value="granted">{t('gov.warrant.extensionGranted')}</MenuItem>
          <MenuItem value="denied">{t('gov.warrant.extensionDenied')}</MenuItem>
        </Select>
      </FormControl>

      {decision === 'granted' ? (
        <TextField
          fullWidth
          required
          size="small"
          type="date"
          label={t('gov.warrant.newExpiry')}
          {...v.field('newExpiry')}
          slotProps={{ inputLabel: { shrink: true } }}
          value={newExpiry}
          onChange={(e) => setNewExpiry(e.target.value)}
        />
      ) : (
        <Alert severity="info">{t('gov.warrant.extensionDeniedNote')}</Alert>
      )}

      <TextField
        fullWidth
        size="small"
        label={t('gov.warrant.decisionNote')}
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />

      <Stack direction="row">
        <Button variant="contained" disabled={decide.isPending} onClick={v.guard(submit)}>
          {t('gov.warrant.extensionDecisionAction')}
        </Button>
      </Stack>
    </Stack>
  );
}

/** The stage of the sub-flow the open warrant is waiting on. */
function OpenWarrantStep({ fileId, warrant }: { fileId: string; warrant: EntryWarrantDetail }) {
  if (warrant.status === 'filed') return <DecisionForm fileId={fileId} warrant={warrant} />;
  if (warrant.status === 'extension_pending') {
    return <ExtensionDecisionForm fileId={fileId} warrant={warrant} />;
  }
  if (warrant.obstruction) return <ObstructionForm fileId={fileId} warrant={warrant} />;
  return <ExecutionForm fileId={fileId} warrant={warrant} />;
}

export function EntryWarrantPanel({
  fileId,
  triggerPhase,
}: {
  fileId: string;
  triggerPhase: WarrantTriggerPhase;
}) {
  const { t } = useTranslation();
  const { data, isError } = useWarrants(fileId);
  const [applying, setApplying] = useState(false);

  const warrants = data ?? [];
  const open = warrants.find(isWarrantOpen);

  return (
    <HairlineCard sx={{ p: 2 }}>
      <Stack direction="row" sx={{ alignItems: 'baseline', justifyContent: 'space-between' }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Icons.Lock size={iconSize.md} />
          <Typography variant="overline">{t('gov.warrant.subflowTitle')}</Typography>
        </Stack>
        {/* Discreet inline statutory reference (design §7). */}
        <Typography variant="caption" color="text.secondary">
          {open?.applicableStatute ?? t('gov.warrant.statuteRef')}
        </Typography>
      </Stack>

      {isError && <Alert severity="error">{t('gov.warrant.loadError')}</Alert>}

      {open ? (
        <Stack spacing={1.5} sx={{ mt: 1 }}>
          {/* The pause is the whole point of the sub-flow — say it plainly. */}
          <Alert severity="warning" icon={<Icons.Clock size={iconSize.lg} />}>
            <Typography variant="subtitle2">{t('gov.warrant.pausedTitle')}</Typography>
            <Typography variant="body2">{t('gov.warrant.pausedNote')}</Typography>
          </Alert>
          <Stack
            useFlexGap
            direction="row"
            spacing={1}
            sx={{ alignItems: 'center', flexWrap: 'wrap' }}
          >
            <StatusChip status="pending" label={t(`gov.warrant.status.${open.status}`)} />
            <Typography variant="caption" color="text.secondary">
              {t(`gov.warrant.trigger.${open.triggerPhase}`)}
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {open.circumstances}
          </Typography>
          <OpenWarrantStep fileId={fileId} warrant={open} />
        </Stack>
      ) : applying ? (
        <FileWarrantForm
          fileId={fileId}
          triggerPhase={triggerPhase}
          onDone={() => setApplying(false)}
        />
      ) : (
        <Stack spacing={1} sx={{ mt: 1, alignItems: 'flex-start' }}>
          <Typography variant="body2" color="text.secondary">
            {t('gov.warrant.intro')}
          </Typography>
          <Button
            size="small"
            startIcon={<Icons.Plus size={iconSize.md} />}
            onClick={() => setApplying(true)}
          >
            {t('gov.warrant.apply')}
          </Button>
        </Stack>
      )}

      {warrants.length > 0 && (
        <>
          <Divider sx={{ my: 2 }} />
          <WarrantLog fileId={fileId} />
        </>
      )}
    </HairlineCard>
  );
}
